import { DriveFolderUpload } from "@mui/icons-material";
import { Box, Collapse, Divider, FormControlLabel, Stack, Switch, Tooltip } from "@mui/material";
import { memo, useCallback, useMemo, useState } from "react";
import { QuotaUsageConfirmationTextOrModal } from "../../../App/Components/QuotaMessaging/QuotaMessaging";
import { TemplateFieldType } from "../../../App/Enums/TemplateFieldType";
import { useMessageDialogState } from "../../../App/State/Hooks/MessageDialog/useMessageDialog";
import { useMessageScheduleHandlers } from "../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import {
  selectGroupMemberCount,
  selectSelectedCandidateCount,
  selectSelectedCandidatesCountByType,
} from "../../../App/State/Slices/MessageDialogSlice";
import { IMessageContainers } from "../../Enums/MessageEnums";
import { MemoTextTemplateListDialog } from "../../MessageTemplates/TextTemplateListDialog";
import { MessageCalendarOptions } from "../../SendGroupMessage/Components/MessageCalendarOptions/MessageCalendarOptions";
import MessageTemplateFields from "../../SendGroupMessage/Components/MessageTemplateFields/MessageTemplateFields";
import {
  IMessageActionButtonProps,
  MessageActionButton,
} from "../../SendGroupMessage/Components/SharedComponents/MessageActionButton";
import PendingMessageQueue from "../../SendGroupMessage/Components/SharedComponents/PendingMessageQueue";
import { RegulationText } from "../../SendGroupMessage/Components/SharedComponents/RegulationText";
import TextMessageInput from "../../SendGroupMessage/Components/SharedComponents/TextMessageInput";
import { SendMessagesMutationState } from "../State/useMessageMutationState";
import useMessageSendState from "../State/useMessageSendState";
import { useValidateTagsAndRecipients } from "./Hooks/useValidateTagsAndRecipients";
import { Recipients } from "./Recipients";
import { ToggleNotificationAlerts } from "./ToggleNotificationAlerts";

export interface ITextMessageSendOrScheduleProps {
  messageMutationState: SendMessagesMutationState;
  isGroup: boolean;
  notifyMe: boolean;
  setNotifyMe: (value: boolean) => void;
  disableNotificationToggle?: boolean;
}

export default function TextMessageSendOrSchedule({
  messageMutationState,
  isGroup,
  notifyMe,
  setNotifyMe,
  disableNotificationToggle = false,
}: ITextMessageSendOrScheduleProps) {
  // Hooks - Message state and handlers
  const {
    getters: {
      isScheduled,
      disableSaveMessageAction,
      disableSaveMessageMessage,
      disableScheduleAction,
      disableAddToQueueAction,
      scheduleMessagesTooltipText,
      disabledScheduleMessagesTooltipText,
      addButtonTooltipText,
      postSendMessageIsLoading,
      upsertMessageTemplateLoading,
    },
    handlers: { setIsScheduled, handleSendNow, handleScheduleMessages },
  } = useMessageSendState(messageMutationState);

  const { containerType } = useMessageDialogState();
  const { handleClickScheduleButton } = useMessageScheduleHandlers();

  // Recipient counts
  const selectedCandidateCount = useAppSelector(selectSelectedCandidateCount);
  const groupMemberCount = useAppSelector(selectGroupMemberCount);
  const selectedTextCandidateCount = useAppSelector((state) =>
    selectSelectedCandidatesCountByType(state, "text"),
  );
  const sendCount = useMemo(
    () => (isGroup ? selectedCandidateCount || groupMemberCount : selectedTextCandidateCount),
    [isGroup, selectedCandidateCount, groupMemberCount, selectedTextCandidateCount],
  );

  // Validation state
  const {
    disabledTooltipText,
    disabledScheduleMessagesTooltip,
    hasValidRecipients,
    hasTitleError,
    hasAreaOfInterestError,
    areaOfInterestError,
    recruiterTitleError,
  } = useValidateTagsAndRecipients(
    disableSaveMessageMessage || "Send Message Now",
    disabledScheduleMessagesTooltipText,
    isGroup,
  );

  // UI state flags
  const hasValidationErrors = !hasValidRecipients || hasTitleError || hasAreaOfInterestError;
  const hasNoSelectedCandidates = !isGroup && selectedTextCandidateCount === 0;
  const disableTextInput = useMemo(
    () => hasNoSelectedCandidates || upsertMessageTemplateLoading || postSendMessageIsLoading,
    [hasNoSelectedCandidates, upsertMessageTemplateLoading, postSendMessageIsLoading],
  );
  const showRecipients = !isGroup && !IMessageContainers[containerType].includes("Candidate");

  // Confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const openConfirmation = () => setConfirmationModalOpen(true);

  const disableConfirmationAction =
    hasValidationErrors ||
    (sendCount > 0 && (isScheduled ? disableScheduleAction : disableSaveMessageAction));

  const confirmationAction = isScheduled ? handleScheduleMessages : handleSendNow;

  const textContentErrorMessage = disableConfirmationAction
    ? hasTitleError
      ? recruiterTitleError
      : hasAreaOfInterestError
        ? areaOfInterestError
        : undefined
    : undefined;

  // Handlers
  const handleToggleScheduled = () => {
    setIsScheduled((prev) => !prev);
    handleClickScheduleButton();
  };

  const handleConfirmationActionAndClose = useCallback(async () => {
    await confirmationAction();
    setConfirmationModalOpen(false);
  }, [confirmationAction]);

  // Shared notification alerts component
  const notificationAlerts = (isMultiMessage: boolean) => (
    <ToggleNotificationAlerts
      notifyMe={notifyMe}
      setNotifyMe={setNotifyMe}
      disableNotificationToggle={disableNotificationToggle}
      isMultiMessage={isMultiMessage}
    />
  );

  // Send button props for "Send Now" action
  const sendNowButtonProps = {
    buttonText: "Send Now",
    disableButton: hasValidationErrors || disableSaveMessageAction,
    tooltipText: "Send Message Now",
    disabledTooltipText: disabledTooltipText,
    "data-test": "group-send-now",
    isGroup,
    sendCount,
  } as IMessageActionButtonProps;

  // Render schedule toggle with send/schedule modes
  const renderScheduleMode = () => (
    <Stack spacing={2} direction="row" alignItems="flex-start">
      <Tooltip title="Schedule messages to be sent at a later time" arrow>
        <FormControlLabel
          control={
            <Switch
              data-test="schedule-messages-switch"
              checked={isScheduled}
              onChange={handleToggleScheduled}
              inputProps={{ "aria-label": "controlled" }}
              color="secondary"
            />
          }
          label="Schedule Messages"
          sx={{ flexGrow: { xs: 1, sm: 0 } }}
        />
      </Tooltip>
      <Stack direction="column" sx={{ flexGrow: 1 }}>
        <Collapse in={isScheduled}>
          <MessageCalendarOptions
            addButtonTooltipText={addButtonTooltipText}
            disableAddToQueueAction={disableAddToQueueAction}
          />
        </Collapse>
        <Collapse in={!isScheduled}>
          <MessageActionButton
            {...sendNowButtonProps}
            onClick={openConfirmation}
            ToggleNotificationAlerts={notificationAlerts(false)}
          />
        </Collapse>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Box>
        {showRecipients && <Recipients />}
        <Stack
          direction="row"
          alignItems="flex-end"
          justifyContent="flex-end"
          divider={<Divider orientation="vertical" flexItem />}
          spacing={2}
          sx={{ transform: "translateY(-0.5em)" }}
        >
          <MemoTextTemplateListDialog
            ButtonProps={{ startIcon: <DriveFolderUpload /> }}
            buttonName={"Load Text Templates"}
          />
          <MessageTemplateFields excludedTypes={[TemplateFieldType.Email, TemplateFieldType.Forms]} />
        </Stack>

        <Box>
          <TextMessageInput disabledInput={disableTextInput} errorMessage={textContentErrorMessage} />
          <Box style={{ marginTop: "20px" }}>{renderScheduleMode()}</Box>
        </Box>

        <Collapse in={isScheduled} unmountOnExit>
          <Box>
            <Stack spacing={2} direction="column" alignItems="flex-start" width="100%">
              <PendingMessageQueue />
              <MessageActionButton
                buttonText="Schedule Messages"
                disableButton={hasValidationErrors || disableScheduleAction}
                tooltipText={scheduleMessagesTooltipText}
                disabledTooltipText={disabledScheduleMessagesTooltip}
                data-test="schedule-message-button"
                isGroup={isGroup}
                onClick={openConfirmation}
                sendCount={sendCount}
                sx={{ width: "100%" }}
                ToggleNotificationAlerts={notificationAlerts(true)}
              />
            </Stack>
          </Box>
        </Collapse>

        <Box sx={{ display: "flex", alignItems: "flex-end", pt: 1, flexDirection: "column" }}>
          <RegulationText />
          <QuotaUsageConfirmationTextOrModal
            channel="text"
            isOpen={confirmationModalOpen}
            setIsOpen={setConfirmationModalOpen}
            isGroup={isGroup}
            selectedCandidatesCount={sendCount}
            isScheduled={isScheduled}
            disableAction={disableConfirmationAction}
            confirmationAction={handleConfirmationActionAndClose}
          />
        </Box>
      </Box>
    </>
  );
}

export const MemoGroupTextInput = memo(TextMessageSendOrSchedule);
